// Plain-English labels + colors for the warehouse manufacturing flow.
// Keeping this in one place so every hub speaks the same language to the
// shop-floor user (Worker / Packager / Warehouse Manager). Enum-y words
// like "PENDING_PACKAGER" never reach the UI — they're translated here.
import { RunStatus, LotStatus, ReturnKind } from '../types';
import { fmtCount, fmtLb } from './units';

// ─── Production run lifecycle ─────────────────────────────────────────────
// Shown to Worker, Packager, and WM. Same words, same colors, everywhere.
export const RUN_STATUS_LABEL: Record<RunStatus, string> = {
    [RunStatus.PENDING_PACKAGER]: 'Waiting for review',
    [RunStatus.PACKED]: 'Packed — ready to send',
    [RunStatus.CONFIRMED]: 'In stock',
    [RunStatus.REJECTED]: 'Needs fixing',
};

// Short version for badges when space is tight
export const RUN_STATUS_SHORT: Record<RunStatus, string> = {
    [RunStatus.PENDING_PACKAGER]: 'Waiting',
    [RunStatus.PACKED]: 'Packed',
    [RunStatus.CONFIRMED]: 'In stock',
    [RunStatus.REJECTED]: 'Needs fix',
};

// Pill colors — soft pastels, easy to distinguish at a glance
export const RUN_STATUS_PILL: Record<RunStatus, string> = {
    [RunStatus.PENDING_PACKAGER]: 'bg-amber-100 text-amber-800 border-amber-200',
    [RunStatus.PACKED]: 'bg-blue-100 text-blue-800 border-blue-200',
    [RunStatus.CONFIRMED]: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    [RunStatus.REJECTED]: 'bg-rose-100 text-rose-800 border-rose-200',
};

// ─── Lot lifecycle ────────────────────────────────────────────────────────
export const LOT_STATUS_LABEL: Record<LotStatus, string> = {
    [LotStatus.RECEIVED]: 'New (not started)',
    [LotStatus.IN_PRODUCTION]: 'Being used',
    [LotStatus.DEPLETED]: 'All used up',
    [LotStatus.CLOSED_WITH_VARIANCE]: 'Closed with leftover write-off',
};
export const LOT_STATUS_PILL: Record<LotStatus, string> = {
    [LotStatus.RECEIVED]: 'bg-blue-100 text-blue-800 border-blue-200',
    [LotStatus.IN_PRODUCTION]: 'bg-amber-100 text-amber-800 border-amber-200',
    [LotStatus.DEPLETED]: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    [LotStatus.CLOSED_WITH_VARIANCE]: 'bg-rose-100 text-rose-800 border-rose-200',
};

export const RETURN_KIND_LABEL: Record<ReturnKind, string> = {
    [ReturnKind.BULK]: 'Bulk material',
    [ReturnKind.CRUMBS]: 'Leftover crumbs',
};

// ─── Common formatters ───────────────────────────────────────────────────
// One quantity formatter for every lot-scoped display: weight lots render
// grams, count-based lots (countUnit set) render "50 bundles". Pass the
// lot's/assignment's countUnit wherever the number belongs to a lot.
export const fmtQty = (n: number, countUnit?: string) =>
    countUnit ? fmtCount(n, countUnit) : fmtGrams(n);

// Per-pack SIZE (one pack's weight) in the shop's working unit — pounds for
// weight runs, the count unit ("bundles") otherwise. Use for run/output rows
// that showed a raw "{unitGrams}g".
export const fmtPackSize = (unitGrams: number, countUnit?: string) =>
    countUnit ? `${unitGrams} ${countUnit}` : fmtLb(unitGrams);

// DISPLAY DEFAULT IS POUNDS. The warehouse works in lb, so this — the single
// weight formatter used across the whole pipeline — now renders pounds
// ("8.4 lb"), NOT grams. The argument is still canonical grams; only the output
// unit changed, so every call site flips at once. Per-pack product specs (e.g.
// a "10g pre-roll" size) are rendered inline as grams and never go through here.
export const fmtGrams = (g: number) => fmtLb(g);

// Escape hatch for the rare spot that genuinely must show grams (fine-grained
// variance/debug). Most sites should stay on fmtGrams (pounds).
export const fmtGramsExact = (g: number) => {
    const n = g || 0;
    return Math.abs(n - Math.round(n)) < 0.05 ? `${Math.round(n)}g` : `${n.toFixed(1)}g`;
};
export const fmtDate = (ts: number) => new Date(ts).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
});
export const fmtDateLong = (ts: number) => new Date(ts).toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
});

// A short helper for "how many minutes/hours ago" — friendlier than a full timestamp
// for the very freshest events.
export const fmtRelative = (ts: number) => {
    const diff = Date.now() - ts;
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return fmtDate(ts);
};
